import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { scoreToRiskLevel } from '@safetyhub/shared';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';

export interface SeverityColor {
  /** 기본 채우기 색상 (opacity 0.20) */
  fill: string;
  /** 선택 상태 채우기 색상 (opacity 0.35) */
  selectedFill: string;
  /** 테두리/라벨 배경 색상 */
  stroke: string;
}

const GRAY_COLOR: SeverityColor = {
  fill: 'rgba(156, 163, 175, 0.20)',
  selectedFill: 'rgba(156, 163, 175, 0.35)',
  stroke: '#6b7280',
};

/**
 * score+config 기반 색상 반환 (N5b 정책)
 * score가 없거나 config가 없으면 gray fallback
 */
export function getScoreColor(
  likelihoodScore: number | null | undefined,
  severityScore: number | null | undefined,
  config: RiskAssessmentConfig | null | undefined,
  riskLevelScore?: number | null,
): SeverityColor {
  if (likelihoodScore == null || severityScore == null || !config) {
    return GRAY_COLOR;
  }
  const result = scoreToRiskLevel(
    likelihoodScore,
    severityScore,
    config,
    riskLevelScore ?? undefined,
  );
  const hex = result?.color;
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return GRAY_COLOR;

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    fill: `rgba(${r}, ${g}, ${b}, 0.20)`,
    selectedFill: `rgba(${r}, ${g}, ${b}, 0.35)`,
    stroke: hex,
  };
}

/**
 * 심각도에 따른 색상 반환
 *
 * HazardOverlay, InteractiveHazardEditor 공용
 */
export function getSeverityColor(severity: Severity): SeverityColor {
  switch (severity) {
    case 'CRITICAL':
      return {
        fill: 'rgba(239, 68, 68, 0.20)',
        selectedFill: 'rgba(239, 68, 68, 0.35)',
        stroke: '#dc2626',
      };
    case 'HIGH':
      return {
        fill: 'rgba(249, 115, 22, 0.20)',
        selectedFill: 'rgba(249, 115, 22, 0.35)',
        stroke: '#ea580c',
      };
    case 'MEDIUM':
      return {
        fill: 'rgba(234, 179, 8, 0.20)',
        selectedFill: 'rgba(234, 179, 8, 0.35)',
        stroke: '#ca8a04',
      };
    case 'GOOD':
      return {
        fill: 'rgba(16, 185, 129, 0.20)',
        selectedFill: 'rgba(16, 185, 129, 0.35)',
        stroke: '#059669',
      };
    case 'LOW':
    default:
      return {
        fill: 'rgba(34, 197, 94, 0.20)',
        selectedFill: 'rgba(34, 197, 94, 0.35)',
        stroke: '#16a34a',
      };
  }
}
